import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Result, Spin } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import axios from 'axios';
import config from '../../config';

// Background SVG - same as CampaignLandingPage
const backgroundSVG = "/images/purespectrum-landing.svg";

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #fff;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0;
`;

const BackgroundContainer = styled.div`
  position: fixed;
  top: 20px;
  left: 20px;
  right: 20px;
  bottom: 20px;
  width: calc(100vw - 40px);
  height: calc(100vh - 40px);
  background-image: url(${backgroundSVG});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 30px;
  overflow: hidden;
  z-index: 0;
  
  @media (max-width: 768px) {
    top: 15px;
    left: 15px;
    right: 15px;
    bottom: 15px;
    width: calc(100vw - 30px);
    height: calc(100vh - 30px);
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    top: 10px;
    left: 10px;
    right: 10px;
    bottom: 10px;
    width: calc(100vw - 20px);
    height: calc(100vh - 20px);
    border-radius: 16px;
  }
`;

const PageWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1440px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 40px 40px;
  box-sizing: border-box;
  
  @media (max-width: 900px) {
    padding: 100px 30px 30px;
  }
  
  @media (max-width: 768px) {
    padding: 90px 24px 30px;
  }
  
  @media (max-width: 480px) {
    padding: 80px 20px 20px;
  }
  
  @media (max-width: 375px) {
    padding: 70px 16px 16px;
  }
`;

const Logo = styled.div`
  position: absolute;
  top: 50px;
  left: 50px;
  height: 32px;
  z-index: 2;
  
  img {
    height: 100%;
    width: auto;
    display: block;
    max-width: 136px;
  }
  
  @media (max-width: 900px) {
    left: 40px;
    top: 40px;
    height: 30px;
    
    img {
      max-width: 128px;
    }
  }
  
  @media (max-width: 768px) {
    top: 30px;
    left: 30px;
    height: 28px;
    
    img {
      max-width: 120px;
    }
  }
  
  @media (max-width: 480px) {
    top: 22px;
    left: 22px;
    height: 24px;
    
    img {
      max-width: 100px;
    }
  }
  
  @media (max-width: 375px) {
    top: 20px;
    left: 20px;
    height: 22px;
    
    img {
      max-width: 90px;
    }
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 24px;
  border: 1px solid rgba(27, 16, 40, 0.1);
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.08);
  padding: 48px 40px;
  max-width: 600px;
  width: 100%;
  text-align: center;
  box-sizing: border-box;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    border-radius: 20px;
    padding: 40px 24px;
    max-width: calc(100% - 8px);
  }
  
  @media (max-width: 480px) {
    border-radius: 16px;
    padding: 32px 20px;
    max-width: 100%;
  }
  
  @media (max-width: 375px) {
    border-radius: 14px;
    padding: 24px 16px;
  }
`;

const SurveyEntry = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Loading survey...');

  useEffect(() => {
    const campaignId = searchParams.get('campaign_id');
    const psid = searchParams.get('psid');
    const psSurveyId = searchParams.get('ps_survey_id');
    const transactionId = searchParams.get('transaction_id');
    const statusParam = searchParams.get('status');
    const psHash = searchParams.get('ps_hash');

    if (!campaignId) {
      setStatus('error');
      setMessage('Invalid survey link. Campaign ID is missing.');
      return;
    }

    // Build the entry URL without ps_hash for hash validation
    // IMPORTANT: Preserve the original parameter order as sent by PureSpectrum
    // The hash is computed on the exact URL PureSpectrum sent, minus the ps_hash param
    const buildEntryUrlWithoutHash = () => {
      let fullUrl = window.location.href;

      // Remove ps_hash parameter using string manipulation to preserve exact order
      // Handle both &ps_hash=xxx and ?ps_hash=xxx cases
      fullUrl = fullUrl.replace(/([&?])ps_hash=[^&]*(&|$)/g, (match, prefix, suffix) => {
        // If ps_hash was the only param after ?, keep the ?
        // If it was in the middle, replace with & or nothing
        if (prefix === '?' && suffix === '&') return '?';
        if (prefix === '&' && suffix === '&') return '&';
        if (prefix === '?' && suffix === '') return '';
        if (prefix === '&' && suffix === '') return '';
        return suffix;
      });

      // Clean up any trailing ? or &
      fullUrl = fullUrl.replace(/[?&]$/, '');

      return fullUrl;
    };

    const handleRedirectToPureSpectrum = async (statusType) => {
      if (!transactionId) return;

      try {
        const response = await axios.get(`${config.api.base_url}/campaigns/purespectrum/redirect-url`, {
          params: {
            transaction_id: transactionId,
            status: statusType
          }
        });

        if (response.data?.data?.redirect_url) {
          window.location.href = response.data.data.redirect_url;
        }
      } catch (error) {
        console.error('Failed to generate redirect URL:', error);
      }
    };

    if (statusParam === 'complete') {
      setStatus('success');
      setMessage('Thank you for completing the survey!');

      setTimeout(() => {
        handleRedirectToPureSpectrum('complete');
      }, 3000);
      return;
    }

    if (statusParam === 'terminated') {
      setStatus('terminated');
      setMessage('Survey was terminated.');

      setTimeout(() => {
        handleRedirectToPureSpectrum('terminated');
      }, 3000);
      return;
    }

    if (statusParam === 'quotafull') {
      setStatus('quotafull');
      setMessage('Survey quota is full. Thank you for your interest.');

      setTimeout(() => {
        handleRedirectToPureSpectrum('quotafull');
      }, 3000);
      return;
    }

    const fetchCampaignAndRedirect = async () => {
      try {
        // Only record entry and validate hash if transaction_id is present (coming from PureSpectrum)
        if (transactionId) {
          // Build params for backend including ps_hash and entry_url for validation
          const recordEntryParams = new URLSearchParams();
          recordEntryParams.append('transaction_id', transactionId);
          if (campaignId) recordEntryParams.append('campaign_id', campaignId);
          if (psid) recordEntryParams.append('psid', psid);
          if (psHash) recordEntryParams.append('ps_hash', psHash);

          // Pass the entry URL for hash validation
          const entryUrl = buildEntryUrlWithoutHash();
          recordEntryParams.append('entry_url', entryUrl);

          const entryResponse = await axios.get(
            `${config.api.base_url}/survey-respondents/entry?${recordEntryParams.toString()}`
          );

          // Check if hash validation failed
          if (!entryResponse.data?.success && entryResponse.data?.data?.redirect_url) {
            // Hash validation failed - redirect to PureSpectrum with status 31
            console.log('Hash validation failed, redirecting to PureSpectrum');
            window.location.href = entryResponse.data.data.redirect_url;
            return;
          }
        }

        // Fetch campaign and redirect to survey
        const response = await axios.get(`${config.api.base_url}/campaigns/public/${campaignId}`);
        const campaignData = response.data?.data || response.data;

        if (campaignData && campaignData.campaign_link) {
          const campaignUrl = `${campaignData.campaign_link}?psid=${psid || ''}&transaction_id=${transactionId || ''}&campaign_id=${campaignId}`;
          window.location.href = campaignUrl;
        } else {
          setStatus('error');
          setMessage('Study link not found.');
        }
      } catch (error) {
        // Check if the error response contains hash failure redirect
        if (error.response?.data?.data?.redirect_url) {
          console.log('Hash validation failed, redirecting to PureSpectrum');
          window.location.href = error.response.data.data.redirect_url;
          return;
        }

        setStatus('error');
        setMessage('Failed to load study. Please try again.');
      }
    };

    fetchCampaignAndRedirect();
  }, [searchParams, navigate]);

  const getResultIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 72 }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 72 }} />;
      case 'terminated':
        return <CloseCircleOutlined style={{ color: '#faad14', fontSize: 72 }} />;
      case 'quotafull':
        return <CloseCircleOutlined style={{ color: '#1890ff', fontSize: 72 }} />;
      default:
        return <Spin size="large" />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'success':
        return 'Survey Completed!';
      case 'error':
        return 'Error';
      case 'terminated':
        return 'Survey Terminated';
      case 'quotafull':
        return 'Quota Full';
      default:
        return 'Processing...';
    }
  };

  return (
    <PageContainer>
      <BackgroundContainer />
      <PageWrapper>
        <Logo>
          <img src="/images/logo.svg" alt="CrowdVox" />
        </Logo>
        <ContentCard>
          <Result
            icon={getResultIcon()}
            title={getTitle()}
            subTitle={message}
          />
        </ContentCard>
      </PageWrapper>
    </PageContainer>
  );
};

export default SurveyEntry;
